import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, inject, output } from '@angular/core';
import { LocalizationModule, LocalizationService } from '@abp/ng.core';
import { CellValueChangedEvent, ColDef } from '@ag-grid-community/core';
import { EMPTY, Observable } from 'rxjs';
import { FieldTypes, MathCellEditorComponent } from '@digiwall/ag-grid';
import { ObserverQueue } from '@digiwall/core';
import {
  InventoryCountDocDto,
  InventoryCountDocLineUpdatedDto,
  InventoryCountDocService,
} from '@digiwall/goliath-documents/proxy';
import { LinesEditorComponent } from '../../../../components';

/**
 * Read-only inventory-count lines grid where only `countedQuantity` and `workerComment` are
 * editable. Each cell edit is persisted immediately through the per-line mutex setters
 * (`setLineCountedQuantity` / `setLineWorkerComment`), mirroring the work-order lines editor.
 */
@Component({
  selector: 'document-inventory-count-doc-lines-grid',
  templateUrl: './inventory-count-doc-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class InventoryCountDocLinesEditorComponent {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  protected _document: InventoryCountDocDto;
  protected _readonly = false;

  @Input() set document(value: InventoryCountDocDto) {
    this._document = value;
  }

  @Input() set readonly(value: boolean) {
    this._readonly = value;
  }

  lineUpdated = output<InventoryCountDocLineUpdatedDto>();

  private localizationService = inject(LocalizationService);
  private service = inject(InventoryCountDocService);
  private updateQueue = inject(ObserverQueue);

  protected columnDefs: ColDef[] = [
    {
      field: 'displayReference',
      headerName: '#',
      type: FieldTypes.String,
      width: 65,
      pinned: 'left',
      editable: false,
    },
    {
      field: 'articleCode',
      headerName: this.localizationService.instant('Doc::Reference'),
      type: FieldTypes.String,
      editable: false,
    },
    {
      field: 'articleDescription',
      headerName: this.localizationService.instant('::Description'),
      type: FieldTypes.String,
      editable: false,
    },
    {
      field: 'storageLocation',
      headerName: this.localizationService.instant('GoliathCore::Company:StorageLocation'),
      type: FieldTypes.String,
      editable: false,
    },
    {
      field: 'systemQuantity',
      headerName: this.localizationService.instant('Doc::InventoryCount:SystemQuantity'),
      type: FieldTypes.Number,
      editable: false,
    },
    {
      field: 'countedQuantity',
      headerName: this.localizationService.instant('Doc::InventoryCount:CountedQuantity'),
      type: FieldTypes.Number,
      editable: () => !this._readonly,
      cellEditor: MathCellEditorComponent,
    },
    {
      field: 'workerComment',
      headerName: this.localizationService.instant('Doc::InventoryCount:WorkerComment'),
      type: FieldTypes.String,
      editable: () => !this._readonly,
    },
    {
      field: 'articleRange1',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRange1'),
      type: FieldTypes.String,
      editable: false,
      hide: true,
    },
    {
      field: 'articleRangeValue1',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue1'),
      type: FieldTypes.String,
      editable: false,
      hide: true,
    },
    {
      field: 'articleRange2',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRange2'),
      type: FieldTypes.String,
      editable: false,
      hide: true,
    },
    {
      field: 'articleRangeValue2',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleRangeValue2'),
      type: FieldTypes.String,
      editable: false,
      hide: true,
    },
    {
      field: 'articleLot',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleLot'),
      type: FieldTypes.String,
      editable: false,
      hide: true,
    },
    {
      field: 'articleSerial',
      headerName: this.localizationService.instant('Doc::StockDoc:ArticleSerial'),
      type: FieldTypes.String,
      editable: false,
      hide: true,
    },
    {
      field: 'length',
      headerName: this.localizationService.instant('Product::Stock:L'),
      type: FieldTypes.Number,
      editable: false,
      hide: true,
    },
    {
      field: 'width',
      headerName: this.localizationService.instant('Product::Stock:W'),
      type: FieldTypes.Number,
      editable: false,
      hide: true,
    },
    {
      field: 'thickness',
      headerName: this.localizationService.instant('Product::Stock:T'),
      type: FieldTypes.Number,
      editable: false,
      hide: true,
    },
  ];

  protected onCellValueChanged = (event: CellValueChangedEvent<any, any>): void => {
    const observable = this.getCellValueChangedObservable(event);

    this.updateQueue
      .enqueue(() => observable)
      .subscribe({
        next: response => {
          if (response) this.applyLineUpdate(response);
        },
        error: () => {
          const oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({ update: [oldData] });
        },
      });
  };

  private getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<InventoryCountDocLineUpdatedDto> {
    switch (event.column.getColId()) {
      case 'countedQuantity':
        return this.service.setLineCountedQuantity(
          this._document.id,
          this._document.concurrencyStamp,
          event.data.id,
          event.data.countedQuantity,
        );
      case 'workerComment':
        return this.service.setLineWorkerComment(
          this._document.id,
          this._document.concurrencyStamp,
          event.data.id,
          event.data.workerComment,
        );
      default:
        return EMPTY;
    }
  }

  private applyLineUpdate(response: InventoryCountDocLineUpdatedDto): void {
    if (response == null) return;
    this._document.concurrencyStamp = response.concurrencyStamp ?? this._document.concurrencyStamp;
    this.lineUpdated.emit(response);
    this.lineGrid.grid.grid.api.applyTransaction({ update: response.updatedLines });
  }
}
